/**
 * Звуки игры.
 *
 * Синтезируются в браузере, а не берутся файлами. Причины две. Первая —
 * происхождение: чужие сэмплы пришлось бы лицензировать и хранить, а тут
 * несколько строк математики. Вторая — родство: все звуки собираются из одного
 * набора (мягкая синусоида, короткое затухание, ноты одного аккорда), поэтому
 * они разные, но слышно, что из одной игры.
 *
 * Ничего не звучит громко и ничего не длится дольше трети секунды: звук здесь
 * подтверждает действие, а не сопровождает его.
 *
 * Браузер не даёт запустить звук до первого действия человека на странице,
 * поэтому контекст создаётся лениво — при первом же вызове из обработчика
 * клика он окажется разрешённым.
 */

const STORAGE_KEY = "chronoguessr.sound";

/** Ноты одного аккорда: звуки перекликаются, а не спорят друг с другом. */
const NOTES = {
  low: 392.0, // соль
  mid: 523.25, // до
  high: 659.25, // ми
  top: 784.0, // соль октавой выше
};

type Voice = {
  frequency: number;
  /** Длительность, секунды. */
  duration: number;
  /** Громкость от 0 до 1. Общий потолок низкий намеренно. */
  gain: number;
  type?: OscillatorType;
  /** Скольжение к другой частоте — им отличаются «поставил» и «ответил». */
  glideTo?: number;
  /** Задержка от начала, секунды: так собираются двузвучия. */
  delay?: number;
};

const VOICES: Record<string, Voice[]> = {
  // Отсчёт перед стартом: три коротких удара и один открытый на «поехали».
  tick: [{ frequency: NOTES.mid, duration: 0.09, gain: 0.16, type: "sine" }],
  go: [
    { frequency: NOTES.high, duration: 0.16, gain: 0.2 },
    { frequency: NOTES.top, duration: 0.22, gain: 0.16, delay: 0.06 },
  ],
  // Метка на карте — короткий щелчок вверх: что-то встало на место.
  pin: [{ frequency: NOTES.high, duration: 0.07, gain: 0.13, glideTo: NOTES.top }],
  // Ползунок года: почти шёпот, иначе перетаскивание превратится в стрельбу.
  year: [{ frequency: NOTES.low, duration: 0.04, gain: 0.05, type: "triangle" }],
  // Ответ отправлен — то же движение, что у метки, но ниже и увереннее.
  answer: [
    { frequency: NOTES.mid, duration: 0.14, gain: 0.18 },
    { frequency: NOTES.top, duration: 0.18, gain: 0.12, delay: 0.05 },
  ],
  // Последние секунды: тот же удар, что в отсчёте, но выше и суше. Он торопит,
  // не пугая: сирена на своей же игре — перебор.
  urgent: [{ frequency: NOTES.top, duration: 0.07, gain: 0.12, type: "sine" }],
  // Нажатие на «Играть»: короткий подъём, как будто что-то раскрывается.
  start: [
    { frequency: NOTES.low, duration: 0.12, gain: 0.16, glideTo: NOTES.mid },
    { frequency: NOTES.high, duration: 0.18, gain: 0.12, delay: 0.07 },
  ],
  // Квест пройден. Три ноты одного аккорда подряд — победно, но негромко:
  // это игра про разглядывание фотографий, а не про фанфары.
  victory: [
    { frequency: NOTES.mid, duration: 0.18, gain: 0.17 },
    { frequency: NOTES.high, duration: 0.18, gain: 0.16, delay: 0.11 },
    { frequency: NOTES.top, duration: 0.42, gain: 0.15, delay: 0.22 },
  ],
  // Комната создана: два тона вверх — место готово.
  roomOpen: [
    { frequency: NOTES.mid, duration: 0.12, gain: 0.14 },
    { frequency: NOTES.top, duration: 0.2, gain: 0.12, delay: 0.08 },
  ],
  // Кто-то пришёл в комнату. Звучит у всех, поэтому тише остальных: событие
  // не про действие человека за экраном, а про чужое.
  roomJoin: [
    { frequency: NOTES.high, duration: 0.1, gain: 0.1 },
    { frequency: NOTES.mid, duration: 0.16, gain: 0.09, delay: 0.07 },
  ],
  // Время вышло: два спада вниз. Не тревожная сирена — сообщение, а не укор.
  timeUp: [
    { frequency: NOTES.high, duration: 0.16, gain: 0.16, glideTo: NOTES.mid },
    { frequency: NOTES.mid, duration: 0.26, gain: 0.14, glideTo: NOTES.low, delay: 0.14 },
  ],
};

export type SoundName = keyof typeof VOICES;

let context: AudioContext | null = null;
let enabled = readEnabled();

function readEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== "off";
  } catch {
    // Приватное окно или запрет на хранилище: звук остаётся включённым.
    return true;
  }
}

export function soundEnabled(): boolean {
  return enabled;
}

export function setSoundEnabled(value: boolean): void {
  enabled = value;
  try {
    localStorage.setItem(STORAGE_KEY, value ? "on" : "off");
  } catch {
    // Выбор проживёт до конца вкладки.
  }
}

function audio(): AudioContext | null {
  if (context) return context;

  const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;

  try {
    context = new Ctor();
  } catch {
    return null;
  }
  return context;
}

/**
 * Проиграть звук. Ошибки глушатся молча: отсутствие звука — не повод ломать
 * ход игры, и уж точно не повод писать в консоль на каждый клик.
 */
export function play(name: SoundName): void {
  if (!enabled) return;

  const ctx = audio();
  if (!ctx) return;

  // Вкладка могла уснуть, и контекст вместе с ней. Возобновление
  // асинхронное, но звук всё равно ставится в очередь на текущее время.
  if (ctx.state === "suspended") void ctx.resume();

  const now = ctx.currentTime;
  for (const voice of VOICES[name]) {
    try {
      const start = now + (voice.delay ?? 0);
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = voice.type ?? "sine";
      oscillator.frequency.setValueAtTime(voice.frequency, start);
      if (voice.glideTo) {
        oscillator.frequency.exponentialRampToValueAtTime(voice.glideTo, start + voice.duration);
      }

      // Мгновенный старт щёлкает, поэтому нарастание короткое, но есть;
      // затухание экспоненциальное — так звук кажется мягче линейного.
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(voice.gain, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + voice.duration);

      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(start);
      oscillator.stop(start + voice.duration + 0.02);
    } catch {
      return;
    }
  }
}

/**
 * Тот же звук, но не чаще, чем раз в интервал.
 *
 * Ползунок года шлёт события десятками в секунду, и без ограничения он
 * звучал бы очередью.
 */
export function throttled(name: SoundName, ms: number): () => void {
  let last = 0;
  return () => {
    const now = Date.now();
    if (now - last < ms) return;
    last = now;
    play(name);
  };
}
